<script lang="ts">
	import type { SanityHero, SanityPicture } from "#lib/types/shared.js";
	import { Button, contentBlockComponents } from "@ishavskraft/shared";
	import { SanityPicture as SanityPictureComponent } from "@ishavskraft/shared";
	import CheckmarkList from "#lib/CheckmarkList.svelte";
	import { breakpoints as br } from "#lib/utils/getBreakpoints.js";
	import { PortableText } from "@portabletext/svelte";
	import sessionValues from "#lib/sessionValues.js";
	import { selectAreas } from "#lib/utils/areaUtils.js";

	const sizes = `(min-width: ${br.desktop}) 40vw, (min-width: ${br.laptop}) 65vw, 100vw`;

	// Land on last-picked area; SSR keeps canonical bare URL for bots.
	const spotprisenHref = $derived.by(() => {
		const stored = $sessionValues.area;
		const match = stored ? selectAreas.find((a) => a.value === stored) : undefined;
		return match ? `/spotprisen/${match.seoSlug}` : "/spotprisen";
	});

	const linkList = $derived([
		{
			label: "Nettbutikk",
			href: "/nettbutikk",
			description: "Varmepumper, elbilladere, og mer",
			icon: "ShopIcon",
		},
		{
			label: "Strømpris",
			href: spotprisenHref,
			description: "Dagens eller historisk spotpris",
			icon: "BarChartIcon",
		},
		{
			// Named for the hub it opens, not the FAQ it links on to.
			label: "Kundeservice",
			href: "/privat/kundeservice",
			description: "Finn svar eller ta kontakt",
			icon: "CustomerServiceIcon",
		},
	]);

	interface Props {
		headingElement: string;
		pictureObject?: SanityPicture | null;
		hero?: SanityHero;
	}

	let { headingElement, pictureObject = null, hero }: Props = $props();
</script>

<div
	class="front-page-hero grid children-m-0 laptop:mx-edge-x laptop:rounded-2xl laptop:mb-10 mt-4 laptop:mt-5 desktop:mt-8 wide:mt-8 laptop:grid-cols-[4fr_7fr] desktop:grid-cols-[8fr_9fr] landscape:text-body relative isolate"
>
	<div
		class="self-end hyphens-manual px-edge-x laptop:pr-0 laptop:pl-0 relative z-2 max-laptop:order-1"
	>
		<svelte:element
			this={headingElement}
			class="text-3xl text-pretty tablet:text-[calc(1.25*var(--font-size-body)+1vw)] desktop:text-h1 my-4"
		>
			{#if hero?.header}
				<PortableText
					value={hero?.header}
					components={contentBlockComponents}
					onMissingComponent={false}
				/>
			{:else}
				Velkommen til et <span class="text-$primary-strong">enkelt</span> kundeforhold
			{/if}
		</svelte:element>

		{#if hero?.content}
			<PortableText
				value={hero.content}
				components={contentBlockComponents}
				onMissingComponent={false}
			/>
		{:else}
			<CheckmarkList class="my-4">
				<li>Hyggelig og personlig kundeservice</li>
				<li>Gode fordelsavtaler på strøm</li>
				<li>Full oversikt i Ishavskraft-appen</li>
				<li>Mange gode produkter for hus og hjem</li>
			</CheckmarkList>
		{/if}

		<p class="mt-8 laptop:mt-12 mb-0">
			<Button
				primary
				class="min-h-[3rem] desktop:min-h-[3.5rem] desktop:px-8 w-full laptop:w-initial inline-flex items-center justify-center plausible-event-name=Strømavtaler+front text-base desktop:text-lg"
				href={hero?.button?.url || "/stromavtale"}
			>
				{#if hero?.button?.text}
					{hero.button.text}
				{:else}
					Finn beste strømavtale hos Ishavskraft
				{/if}
			</Button>
		</p>
	</div>

	<div
		class="picture-fallback-background aspect-[16/8] laptop:aspect-[unset] relative isolate grid items-end max-laptop:mx-edge-x"
		style={pictureObject?.lqip ? `--hero-img-lqip: url("${pictureObject.lqip}")` : undefined}
	>
		{#if pictureObject?.src}
			<div class="picture-container z--1 absolute inset-0 w-full h-full">
				<SanityPictureComponent
					{pictureObject}
					{sizes}
					alt="Ishavskraft"
					class="w-full h-full object-cover object-[center_top]"
					loading="eager"
					--rounded-picture="var(--front-hero-image-rounded)"
				/>
			</div>
		{/if}
	</div>
</div>

<ul
	class="list-none p-0 laptop:my-16 mx-edge-x grid gap-y-3 gap-x-[min(1.5vw,var(--s-8))] laptop:grid-cols-3 items-center"
>
	{#each linkList as link (link)}
		<li>
			<Button href={link.href} class="text-start bg-$bg w-full" arrow>
				{#snippet iconStart()}
					{#if link.icon === "CustomerServiceIcon"}
						<svg
							aria-hidden="true"
							class="icon-support ml-[.25vw] mr-[.75vw]"
							width="39"
							height="34"
							viewBox="0 0 39 34"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M17.625 33.875V30.125H32.625V16.8125C32.625 15 32.2813 13.2969 31.5938 11.7031C30.9063 10.1094 29.9688 8.71875 28.7813 7.53125C27.5938 6.34375 26.2031 5.40625 24.6094 4.71875C23.0156 4.03125 21.3125 3.6875 19.5 3.6875C17.6875 3.6875 15.9844 4.03125 14.3906 4.71875C12.7969 5.40625 11.4062 6.34375 10.2188 7.53125C9.03125 8.71875 8.09375 10.1094 7.40625 11.7031C6.71875 13.2969 6.375 15 6.375 16.8125V28.25H4.5C3.46875 28.25 2.58625 27.8831 1.8525 27.1494C1.1175 26.4144 0.75 25.5312 0.75 24.5V20.75C0.75 20.0312 0.921875 19.3981 1.26563 18.8506C1.60938 18.3044 2.0625 17.8594 2.625 17.5156L2.76562 15.0312C3.04688 12.75 3.69562 10.6875 4.71187 8.84375C5.72687 7 6.9925 5.4375 8.50875 4.15625C10.0238 2.875 11.7269 1.8825 13.6181 1.17875C15.5081 0.47625 17.4688 0.125 19.5 0.125C21.5625 0.125 23.5394 0.47625 25.4306 1.17875C27.3206 1.8825 29.0156 2.8825 30.5156 4.17875C32.0156 5.47625 33.2738 7.03875 34.29 8.86625C35.305 10.695 35.9531 12.7344 36.2344 14.9844L36.375 17.4219C36.9375 17.7031 37.3906 18.1169 37.7344 18.6631C38.0781 19.2106 38.25 19.8125 38.25 20.4688V24.7812C38.25 25.4688 38.0781 26.0781 37.7344 26.6094C37.3906 27.1406 36.9375 27.5469 36.375 27.8281V30.125C36.375 31.1562 36.0081 32.0394 35.2744 32.7744C34.5394 33.5081 33.6562 33.875 32.625 33.875H17.625ZM13.875 20.75C13.3438 20.75 12.8988 20.57 12.54 20.21C12.18 19.8512 12 19.4062 12 18.875C12 18.3438 12.18 17.8981 12.54 17.5381C12.8988 17.1794 13.3438 17 13.875 17C14.4062 17 14.8519 17.1794 15.2119 17.5381C15.5706 17.8981 15.75 18.3438 15.75 18.875C15.75 19.4062 15.5706 19.8512 15.2119 20.21C14.8519 20.57 14.4062 20.75 13.875 20.75ZM25.125 20.75C24.5938 20.75 24.1488 20.57 23.79 20.21C23.43 19.8512 23.25 19.4062 23.25 18.875C23.25 18.3438 23.43 17.8981 23.79 17.5381C24.1488 17.1794 24.5938 17 25.125 17C25.6562 17 26.1019 17.1794 26.4619 17.5381C26.8206 17.8981 27 18.3438 27 18.875C27 19.4062 26.8206 19.8512 26.4619 20.21C26.1019 20.57 25.6562 20.75 25.125 20.75ZM8.29688 17.8438C8.17188 16 8.43 14.3281 9.07125 12.8281C9.71125 11.3281 10.57 10.0544 11.6475 9.00688C12.7262 7.96062 13.9688 7.15625 15.375 6.59375C16.7812 6.03125 18.1875 5.75 19.5938 5.75C22.4375 5.75 24.8988 6.64875 26.9775 8.44625C29.055 10.2425 30.3125 12.4844 30.75 15.1719C27.8125 15.1406 25.1562 14.3513 22.7812 12.8038C20.4062 11.2575 18.6094 9.25 17.3906 6.78125C16.8906 9.3125 15.8362 11.5544 14.2275 13.5069C12.6175 15.4606 10.6406 16.9062 8.29688 17.8438Z"
								fill="#0A66FF"
							/>
						</svg>
					{:else if link.icon === "BarChartIcon"}
						<svg
							aria-hidden="true"
							class="icon-chart ml-[.25vw] mr-[.75vw]"
							width="30"
							height="30"
							viewBox="0 0 30 30"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M7.5 9.375H0V30H7.5V9.375Z" fill="#0A66FF" />
							<path d="M30 16.875H22.5V30H30V16.875Z" fill="#0A66FF" />
							<path d="M18.75 0H11.25V30H18.75V0Z" fill="#0A66FF" />
						</svg>
					{:else if link.icon === "ShopIcon"}
						<svg
							aria-hidden="true"
							class="icon-shop ml-[.25vw] mr-[.75vw]"
							width="28"
							height="35"
							viewBox="0 0 24 30"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M3.5013 29.1663C2.72214 29.1663 2.05536 28.8891 1.50097 28.3348C0.945635 27.7794 0.667969 27.1122 0.667969 26.333V9.33301C0.667969 8.55384 0.945635 7.88706 1.50097 7.33267C2.05536 6.77734 2.72214 6.49967 3.5013 6.49967H6.33464C6.33464 4.94134 6.8895 3.60731 7.99922 2.49759C9.10894 1.38787 10.443 0.833008 12.0013 0.833008C13.5596 0.833008 14.8937 1.38787 16.0034 2.49759C17.1131 3.60731 17.668 4.94134 17.668 6.49967H20.5013C21.2805 6.49967 21.9477 6.77734 22.5031 7.33267C23.0574 7.88706 23.3346 8.55384 23.3346 9.33301V26.333C23.3346 27.1122 23.0574 27.7794 22.5031 28.3348C21.9477 28.8891 21.2805 29.1663 20.5013 29.1663H3.5013ZM7.7513 13.583C8.15269 13.583 8.48939 13.447 8.76139 13.175C9.03244 12.904 9.16797 12.5677 9.16797 12.1663V9.33301H6.33464V12.1663C6.33464 12.5677 6.47064 12.904 6.74264 13.175C7.01369 13.447 7.34991 13.583 7.7513 13.583ZM9.16797 6.49967H14.8346C14.8346 5.72051 14.5574 5.05373 14.0031 4.49934C13.4477 3.94401 12.7805 3.66634 12.0013 3.66634C11.2221 3.66634 10.5554 3.94401 10.001 4.49934C9.44564 5.05373 9.16797 5.72051 9.16797 6.49967ZM16.2513 13.583C16.6527 13.583 16.9894 13.447 17.2614 13.175C17.5324 12.904 17.668 12.5677 17.668 12.1663V9.33301H14.8346V12.1663C14.8346 12.5677 14.9706 12.904 15.2426 13.175C15.5137 13.447 15.8499 13.583 16.2513 13.583Z"
								fill="#0A66FF"
							/>
						</svg>
					{/if}
				{/snippet}

				<h2 class="my-0 text-base desktop:text-body">{link.label}</h2>
				<p class="my-0 font-normal text-sm leading-tight">{link.description}</p>
			</Button>
		</li>
	{/each}
</ul>

<style lang="postcss">
	.front-page-hero {
		--front-hero-image-rounded: 1rem;
		gap: var(--s-2) var(--_gx, 0);
	}

	@media (--laptop) {
		.front-page-hero {
			--_gx: var(--s-8);
		}
	}

	.picture-fallback-background {
		border-radius: var(--front-hero-image-rounded, 1rem);
		overflow: hidden;
	}

	.picture-fallback-background::after {
		background: center top / cover no-repeat var(--hero-img-lqip, var(--img-fallback, transparent));
		content: "";
		display: block;
		inset: 1px; /* 1px smaller to avoid color bleed */
		position: absolute;
		z-index: -3;
	}
</style>
